import React, {
  cloneElement,
  ComponentType,
  createContext,
  createElement,
  forwardRef,
  Fragment,
  HTMLAttributes,
  ReactElement,
  ReactHTML,
  ReactNode,
  Ref,
  useContext,
  useMemo,
  useRef
} from 'react';
import { NO_ELEMENT } from 'utils';
import { registry } from 'components/registry';
import { ReactComponent } from 'models/ReactComponent';
import { ContentArea as ContentAreaModel } from 'models/ContentArea';

export interface RegistryType<TComponentType = ComponentType<unknown>> {
  [key: string]: TComponentType;
}
type Component = Required<Pick<ReactComponent, 'component'>>;
interface ContextType {
  registry: RegistryType;
  custom: RegistryType;
}

const buildRegistry = (
  custom: RegistryType = {}
): { registry: RegistryType; custom: RegistryType } => ({
  registry,
  custom
});

const RegistryCtx = createContext<ContextType>(buildRegistry());

export function useRegistry(): ContextType {
  return useContext(RegistryCtx);
}

export const useComponent = <P>({ component }: Component): ComponentType<P> => {
  const { registry, custom } = useRegistry();
  const getComponent = useMemo(
    () => custom[component] || registry[component] || NO_ELEMENT,
    [component, registry, custom]
  );
  return getComponent as ComponentType<P>;
};

export const Registry = ({
  registry,
  children
}: {
  registry: RegistryType;
  children: ReactNode;
}) => {
  const ref = useRef<{ registry: RegistryType; custom: RegistryType }>();
  function getRegistry() {
    return ref.current !== undefined ? ref.current : (ref.current = buildRegistry(registry));
  }
  return createElement(
    RegistryCtx.Provider,
    {
      value: getRegistry()
    },
    children
  );
};

interface InlineProps {
  tag: keyof ReactHTML | string;
  content: string;
  innerRef: Ref<HTMLElement>;
  children: ReactNode;
}
interface ComponentProps<T = HTMLElement> extends HTMLAttributes<T> {
  component: string;
}

type MarkdownProps<T = HTMLElement> = Partial<ComponentProps<T>> &
  Partial<InlineProps> &
  React.Attributes;

const isComponent = <T>(props: MarkdownProps<T>): props is ComponentProps<T> =>
  Boolean(props.component);

export const isReactComponent = (c: unknown): c is ReactComponent => {
  return (
    (c as ReactComponent).tag !== undefined ||
    (c as ReactComponent).component !== undefined ||
    (c as ReactComponent).content !== undefined
  );
};
const CustomComponent = <T = HTMLElement>(props: MarkdownProps<T>) => {
  const { component, children, innerRef, ...rest } = props;
  const Component = useComponent({ component: component || '' });
  return createElement(Component, rest as React.Attributes | null | undefined, children);
};

const Markdown = <T = HTMLElement>(props: MarkdownProps<T>) => {
  if (isComponent(props)) {
    return CustomComponent(props);
  }

  const { children, innerRef, content, tag, ...tagProps } = props as InlineProps;
  if (content) {
    (tagProps as HTMLAttributes<T>).dangerouslySetInnerHTML = { __html: content };
  }
  return createElement(tag, { ...tagProps, ref: innerRef });
};

export default forwardRef<HTMLElement, MarkdownProps>((props, ref) =>
  createElement(Markdown, { ...props, innerRef: ref })
);

interface ContentAreaItemProps extends ComponentProps {
  wrappingComponent: ContentAreaProps['wrappingComponent'];
}

const ContentAreaItem = ({
  component,
  children,
  wrappingComponent,
  ...rest
}: ContentAreaItemProps) => {
  const Component = useComponent({ component });
  if (!Component) {
    return Component;
  }
  // The component might use it's own logic to determine via "component"
  // what to render, i.e Ad
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const props: any = { ...rest, component };
  return !wrappingComponent
    ? createElement(Component, props, children)
    : cloneElement(
        wrappingComponent,
        wrappingComponent.props,
        createElement(Component, props, children)
      );
};
type ContentAreaProps = ContentAreaModel & {
  wrappingComponent?: ReactElement;
};
export const ContentArea = ({ content, wrappingComponent }: ContentAreaProps) =>
  createElement(
    Fragment,
    {},
    content
      .filter(isComponent)
      .map((props, key) => createElement(ContentAreaItem, { key, wrappingComponent, ...props }))
  );
